import { useTranslation } from '@hooks/useTranslation';
import { User } from '@models/user.model';
import {
  Autocomplete,
  Box,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import CustomModal from '../CustomModal';

export interface RequestExecutionConfirmData {
  assigneeId: string;
  assignerNote?: string;
}

interface RequestExecutionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (data: RequestExecutionConfirmData) => void;
  users: User[];
  loading?: boolean;
  submitting?: boolean;
}

const RequestExecutionModal: React.FC<RequestExecutionModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  users,
  loading = false,
  submitting = false,
}) => {
  const localeProcesses = useTranslation('processes');
  const localeCommon = useTranslation('common');

  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [assignerNote, setAssignerNote] = useState('');

  const handleConfirm = () => {
    if (selectedUser?.userId) {
      onConfirm({
        assigneeId: selectedUser.userId,
        assignerNote: assignerNote.trim() ? assignerNote.trim() : undefined,
      });
      setSelectedUser(null);
      setAssignerNote('');
    }
  };

  const handleClose = () => {
    setSelectedUser(null);
    setAssignerNote('');
    onClose();
  };

  const getUserDisplayName = (user: User) => {
    if (user.firstName || user.lastName) {
      return `${user.firstName || ''} ${user.lastName || ''}`.trim();
    }
    return user.email || '';
  };

  return (
    <CustomModal
      isOpen={isOpen}
      title={localeProcesses['requestExecutionTitle']}
      maxWidth="600px"
      showButtons={true}
      customCancelText={localeCommon['cancel']}
      customConfirmText={localeProcesses['requestExecution']}
      onClose={handleClose}
      onConfirm={handleConfirm}
      showConfirmButton={!loading && users.length > 0}>
      <Box className="py-4">
        <Typography variant="body1" className="mb-4">
          {localeProcesses['selectUserToExecuteProcess']}
        </Typography>

        {loading ? (
          <Box className="flex justify-center py-8">
            <CircularProgress size={32} />
          </Box>
        ) : users.length === 0 ? (
          <Typography variant="body2" color="textSecondary" className="text-center py-4">
            {localeProcesses['noUsersAvailable']}
          </Typography>
        ) : (
          <>
            <Autocomplete
              value={selectedUser}
              onChange={(_, newValue) => setSelectedUser(newValue)}
              options={users}
              getOptionLabel={(option) => getUserDisplayName(option)}
              isOptionEqualToValue={(option, value) => option.userId === value.userId}
              disabled={submitting}
              renderOption={(props, option) => {
                const { key, ...otherProps } = props;
                return (
                  <li key={option.userId} {...otherProps}>
                    <Box className="flex flex-col">
                      <Typography variant="body14semibold">{getUserDisplayName(option)}</Typography>
                      {option.email && (option.firstName || option.lastName) && (
                        <Typography variant="body2" color="textSecondary">
                          {option.email}
                        </Typography>
                      )}
                    </Box>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={localeProcesses['selectAssignee']}
                  placeholder={localeProcesses['selectAssignee']}
                  variant="outlined"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {submitting ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            <Box className="mt-4">
              <TextField
                fullWidth
                multiline
                rows={3}
                value={assignerNote}
                onChange={(e) => setAssignerNote(e.target.value)}
                label={localeProcesses['addNoteOptional']}
                placeholder={localeProcesses['notePlaceholder']}
                variant="outlined"
                disabled={submitting}
              />
            </Box>
          </>
        )}
      </Box>
    </CustomModal>
  );
};

export default RequestExecutionModal;
